import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { useToast } from "@/components/ui/use-toast";
import { Company } from "@/types/company/company.types";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { GeneralData, ActivityType, ValidationErrors, TouchedFields } from "@/types/general-data";
import companyService from "@/services/company/companyService";

export const useGeneralData = (year: string, existingData?: any) => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const { toast } = useToast();

  // Fetch company data to get activityType
  const {
    document: company,
    loading: companyLoading,
    error: companyError,
    refetch: refetchCompany,
  } = useData<Company>({
    collectionName: "companies",
    documentId: claims?.companyId,
  });

  // Fetch stone types
  const {
    data: stoneTypes,
    loading: stoneTypesLoading,
    error: stoneTypesError,
  } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
  });

  // Local state
  const [localData, setLocalData] = useState<GeneralData>({
    year,
    ...existingData,
  });

  const [selectedActivityType, setSelectedActivityType] = useState<ActivityType | "">("");
  const [isUpdatingActivityType, setIsUpdatingActivityType] = useState(false);
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});

  // Use ref to access current localData without causing re-renders
  const localDataRef = useRef(localData);
  useEffect(() => {
    localDataRef.current = localData;
  }, [localData]);

  // Initialize selected activity type when company data loads
  useEffect(() => {
    if (company?.activityType) {
      setSelectedActivityType(company.activityType as ActivityType);
    }
  }, [company?.activityType]);

  // Memoize activeStoneTypes to prevent unnecessary recalculations
  const activeStoneTypes = useMemo(() => stoneTypes?.filter((stoneType) => !stoneType.isArchived) || [], [stoneTypes]);

  const handleInputChange = useCallback(
    (field: keyof GeneralData, value: string | number) => {
      setLocalData((prev) => {
        const updated = {
          ...prev,
          [field]: value,
        };

        // If stone type is being changed, update the stone density
        if (field === "stoneTypeId" && typeof value === "string") {
          const selectedStoneType = activeStoneTypes.find((st) => st.id === value);
          updated.stoneDensity = selectedStoneType?.dens_pedra?.value;
        }

        return updated;
      });

      // Batch state update: Only clear error if it exists
      setFieldErrors((prev) => {
        if (prev[field]) {
          return {
            ...prev,
            [field]: "",
          };
        }
        return prev;
      });
    },
    [activeStoneTypes]
  );

  const handleInputBlur = useCallback(
    (field: keyof GeneralData, value: string | number, isRequired: boolean = true) => {
      setTouchedFields((prev) => ({
        ...prev,
        [field]: true,
      }));

      const currentData = localDataRef.current;

      if (isRequired && (!value || value === "" || value === 0)) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: t("validation.required"),
        }));
      } else if (typeof value === "number" && value < 0) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: t("validation.positiveNumber"),
        }));
      } else if (field === "rend" && typeof value === "number" && (value <= 0 || value > 100)) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: t("validation.percentageBetween0And100"),
        }));
      } else if (
        field === "pvp_barato_ext" &&
        typeof value === "number" &&
        currentData.pvp_caro_ext &&
        value > currentData.pvp_caro_ext
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: t("validation.cheapProductMustBeLower"),
        }));
      } else if (
        field === "pvp_caro_ext" &&
        typeof value === "number" &&
        currentData.pvp_barato_ext &&
        value < currentData.pvp_barato_ext
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: t("validation.expensiveProductMustBeHigher"),
        }));
      } else if (
        field === "pvp_barato_trsf" &&
        typeof value === "number" &&
        currentData.pvp_caro_trsf &&
        value > currentData.pvp_caro_trsf
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: t("validation.cheapProductMustBeLower"),
        }));
      } else if (
        field === "pvp_caro_trsf" &&
        typeof value === "number" &&
        currentData.pvp_barato_trsf &&
        value < currentData.pvp_barato_trsf
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: t("validation.expensiveProductMustBeHigher"),
        }));
      } else {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }

      // Cross-validate the other PVP field when one changes (for extraction)
      if (
        field === "pvp_caro_ext" &&
        typeof value === "number" &&
        currentData.pvp_barato_ext &&
        value >= currentData.pvp_barato_ext
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          pvp_barato_ext: "",
        }));
      } else if (
        field === "pvp_barato_ext" &&
        typeof value === "number" &&
        currentData.pvp_caro_ext &&
        value <= currentData.pvp_caro_ext
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          pvp_caro_ext: "",
        }));
      }

      // Cross-validate the other PVP field when one changes (for transformation)
      if (
        field === "pvp_caro_trsf" &&
        typeof value === "number" &&
        currentData.pvp_barato_trsf &&
        value >= currentData.pvp_barato_trsf
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          pvp_barato_trsf: "",
        }));
      } else if (
        field === "pvp_barato_trsf" &&
        typeof value === "number" &&
        currentData.pvp_caro_trsf &&
        value <= currentData.pvp_caro_trsf
      ) {
        setFieldErrors((prev) => ({
          ...prev,
          pvp_caro_trsf: "",
        }));
      }
    },
    [t]
  );

  const handleSelectBlur = useCallback(
    (field: keyof GeneralData, value: string, isRequired: boolean = true) => {
      setTouchedFields((prev) => ({
        ...prev,
        [field]: true,
      }));

      if (isRequired && (!value || value === "")) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: t("validation.required"),
        }));
      } else {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    },
    [t]
  );

  const handleActivityTypeChange = useCallback((activityType: ActivityType) => {
    setSelectedActivityType(activityType);
  }, []);

  const handleSaveActivityType = useCallback(async () => {
    if (!company?.id || !selectedActivityType) return;

    setIsUpdatingActivityType(true);
    try {
      await companyService.edit(company.id, { activityType: selectedActivityType }, claims?.companyId);

      // Refetch company data to get the updated information
      refetchCompany();

      toast({
        title: t("success"),
        description: t("footprints.generalData.activityTypeUpdated"),
        variant: "default",
      });
    } catch (error) {
      console.error("Error updating activity type:", error);
      toast({
        title: t("error"),
        description: t("footprints.generalData.activityTypeUpdateError"),
        variant: "destructive",
      });
    } finally {
      setIsUpdatingActivityType(false);
    }
  }, [company?.id, selectedActivityType, claims?.companyId, refetchCompany, toast, t]);

  // Memoize className function to prevent recreation on every render
  const getInputClassName = useCallback(
    (field: string, baseClassName: string = "h-12") => {
      const hasError = touchedFields[field] && fieldErrors[field];
      return `${baseClassName}${hasError ? " border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`;
    },
    [touchedFields, fieldErrors]
  );

  const getFieldError = useCallback(
    (field: string) => {
      const hasError = touchedFields[field] && fieldErrors[field];
      return hasError ? fieldErrors[field] : null;
    },
    [touchedFields, fieldErrors]
  );

  const checkCompletion = useCallback(
    (activityType: ActivityType | undefined, onComplete?: () => void, onIncomplete?: () => void) => {
      if (!activityType) {
        if (onIncomplete) {
          onIncomplete();
        }
        return;
      }

      // Check if there are any validation errors
      const hasValidationErrors = Object.values(fieldErrors).some((error) => error !== "");

      if (hasValidationErrors) {
        if (onIncomplete) {
          onIncomplete();
        }
        return;
      }

      // Use ref to get current data without dependency issues
      const currentData = localDataRef.current;

      // Check required fields based on activity type
      let isComplete = true;

      if (activityType === "extraction" || activityType === "extraction_and_transformation") {
        isComplete = isComplete && currentData.stoneTypeId !== undefined && currentData.stoneTypeId !== "";
        isComplete = isComplete && currentData.prod_ext !== undefined && currentData.prod_ext > 0;
        isComplete = isComplete && currentData.fat_ext !== undefined && currentData.fat_ext > 0;
        isComplete = isComplete && currentData.pvp_caro_ext !== undefined && currentData.pvp_caro_ext > 0;
        isComplete = isComplete && currentData.pvp_barato_ext !== undefined && currentData.pvp_barato_ext > 0;
      }

      if (activityType === "transformation" || activityType === "extraction_and_transformation") {
        isComplete = isComplete && currentData.prod_trsf !== undefined && currentData.prod_trsf > 0;
        isComplete = isComplete && currentData.fat_trsf !== undefined && currentData.fat_trsf > 0;
        isComplete = isComplete && currentData.pvp_caro_trsf !== undefined && currentData.pvp_caro_trsf > 0;
        isComplete = isComplete && currentData.pvp_barato_trsf !== undefined && currentData.pvp_barato_trsf > 0;
        isComplete = isComplete && currentData.rend !== undefined && currentData.rend > 0;
      }

      if (isComplete && onComplete) {
        onComplete();
      } else if (!isComplete && onIncomplete) {
        onIncomplete();
      }
    },
    [fieldErrors]
  );

  return {
    // Data
    localData,
    company,
    activeStoneTypes,

    // Loading states
    companyLoading,
    stoneTypesLoading,
    isUpdatingActivityType,

    // Error states
    companyError,
    stoneTypesError,
    fieldErrors,
    touchedFields,

    // Activity type state
    selectedActivityType,

    // Handlers
    handleInputChange,
    handleInputBlur,
    handleSelectBlur,
    handleActivityTypeChange,
    handleSaveActivityType,

    // Utilities
    getInputClassName,
    getFieldError,
    checkCompletion,
  };
};
